import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import { calcularSalarioRealidadeBrasileira } from "../utils/SalarioFunctions";
import { 
  SalarioInputData, 
  SalarioResultsType, 
  ValidationMessages
} from "../types.d";
import { ESTADOS_BRASIL } from "../data/constants";

const CALCULATOR_KEY = "calculadora-salario-realidade-brasileira";

const DEFAULT_VALUES: SalarioInputData = {
  salario: "",
  estado: "",
} as const;

const parseNumericValue = (value: string): number => {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

export const SalarioInputCard = ({
  setResults,
}: {
  setResults: (value: SalarioResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<SalarioInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof SalarioInputData>(field: K, value: SalarioInputData[K]) => {
    setFormData((prev: SalarioInputData) => ({ ...prev, [field]: value }));
  };

  const validateSalario = (): string | null => {
    if (!formData.salario) {
      return ValidationMessages.SALARIO_OBRIGATORIO;
    }
    
    const salarioNum = parseNumericValue(formData.salario);
    if (salarioNum <= 0) {
      return ValidationMessages.SALARIO_INVALIDO;
    }
    
    if (salarioNum > 100000) {
      return ValidationMessages.SALARIO_MUITO_ALTO;
    }
    
    return null;
  };

  const validateEstado = (): string | null => {
    if (!formData.estado) {
      return ValidationMessages.ESTADO_OBRIGATORIO;
    }
    
    return null;
  };

  const validateForm = (): string | null => {
    return validateSalario() || validateEstado();
  };

  const executeCalculation = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      const resultado = calcularSalarioRealidadeBrasileira(
        formData.salario,
        formData.estado
      );

      setResults(resultado);
      setMessage("");
    } catch (error) {
      console.error('Erro no executeCalculation:', error);
      setMessage("Erro no cálculo: " + (error as Error).message);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      salario: formData.salario,
      estado: formData.estado,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(executeCalculation);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (!savedData) return;
    
    setFormData({
      salario: (savedData.salario as string) || DEFAULT_VALUES.salario,
      estado: (savedData.estado as string) || DEFAULT_VALUES.estado,
    });
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Seu salário diante da realidade brasileira</MainCardTitle>
      
      <div className="px-4 pb-6">
        <p className="text-gray-700 text-sm mb-6">
          É senso comum: os rendimentos dos brasileiros são baixos e insuficientes. 
          Mas você tem ideia de como o seu salário está em relação ao do restante da população?
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <MainCardContent>
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="salario">
              Qual é o seu salário mensal?
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="salario"
                placeholder="1.000,00"
                type="text"
                value={formData.salario}
                onChange={(event) => updateFormField('salario', sanitizeBrlValue(event))}
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="estado">
              Em qual estado você vive?
            </label>
            <select
              className="w-full p-2 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              id="estado"
              value={formData.estado}
              onChange={(e) => updateFormField('estado', e.target.value)}
            >
              <option value="">Selecione o estado</option>
              {ESTADOS_BRASIL.map((uf) => (
                <option key={uf.value} value={uf.value}>
                  {uf.label}
                </option>
              ))}
            </select>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified || isCalculating}
            type="submit"
            variant="default"
            className="sm:w-[150px]"
          >
            {isCalculating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Calculando...
              </>
            ) : (
              "Calcular"
            )}
          </Button>

          <Button onClick={clear} variant="ghost" type="button">
            Limpar
          </Button>
        </div>
      </form>
      
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};